import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useCurrentUserQuery } from '@/redux/services/portal/user.api';
import { useLocalAuditLogger } from '@/hooks/useLocalAuditLogger';
import * as enums from '../enums/Enums';
import { useLoadingState } from '@/contexts/LoadingContext';
import { useLogout } from '@/contexts/LogoutContext';
import { useDispatch } from 'react-redux';
import { setUserProfile } from '@/redux/reducers/userProfile.reducer';
import * as config from '@/config/env.config';
import { getSafeReturnUrl } from '@/utils/redirectUrl';
import { setBinfAuthCookie } from '@/utils/auth0Token';

export const Callback = () => {
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();
	const { isAuthenticated, user, isLoading: auth0IsLoading, error: auth0Error, getAccessTokenSilently } = useAuth0();
	const { isLoggingOut } = useLogout();
	const logger = useLocalAuditLogger();
	
	// Use Auth0 session state (not localStorage token polling) so /users/current runs as soon as
	// Auth0 finishes the callback — avoids an infinite spinner when the token is not cached yet.
	const { data: userData, isLoading: userIsLoading, error: userError } = useCurrentUserQuery('', {
		skip: auth0IsLoading || !isAuthenticated,
	});

	// Check for email verification error in URL parameters FIRST (before any other processing)
	// This matches the old behavior where error check happened before session processing
	// Universal Login uses query parameters (not hash fragments like old Classic Login)
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const error = urlParams.get('error');
		const errorDescription = urlParams.get('error_description');
		
		if (error && errorDescription) {
			const decodedError = decodeURIComponent(errorDescription);
			// Check if this is an email verification error (same check as old version)
			if (decodedError.includes("email we have sent you to continue to login.") || 
			    decodedError.includes("email we have sent you")) {
				history.replace("/verifyemail");
				return;
			}
		}
	}, [location.search, history]);

	// Check if this is a new signup with unverified email
	// This handles the case where Auth0 authenticates the user but email isn't verified yet
	useEffect(() => {
		// Only check after Auth0 has finished loading and user is authenticated
		if (auth0IsLoading) {
			return;
		}

		// Check if this is a signup action
		const isSignup = localStorage.getItem('signupAction') === 'create';
		
		// Check if user is authenticated but email is not verified
		if (isAuthenticated && user && isSignup && user.email_verified === false) {
			history.replace("/verifyemail");
			return;
		}
	}, [auth0IsLoading, isAuthenticated, user, history]);

	const logUserLogin = () => {
		if (localStorage.getItem('signupAction') === 'create') {
			logger.logNewUser();
		} else if (localStorage.getItem('lastAuthAction') === 'login') {
			logger.logUserAction(enums.AUDITLOG_USERACTION_TYPES.LOGIN);
		}
		localStorage.removeItem('lastAuthAction');
		localStorage.removeItem('signupAction');
	};

	// Extract user info from Auth0 user object and set in Redux when authentication completes
	useEffect(() => {
		if (!auth0IsLoading && isAuthenticated && user) {
			// Extract user info from Auth0 user object and set in Redux (like old version did)
			try {
				const username = user.nickname || 
				                user.name?.split('@')[0] || 
				                user.email?.split('@')[0] || 
				                null;
				const email = user.email || null;
				const avatar = user.picture || null;
				const signupSource = user['https://portal.metabolon.com/user_metadata']?.signupSource || null;

				// Set user profile in Redux immediately after authentication
				dispatch(setUserProfile({
					username,
					email,
					avatar,
					signupSource
				}));
			} catch (error) {
				console.error('Error setting user profile from Auth0 in Callback:', error);
			}
		}
	}, [auth0IsLoading, isAuthenticated, user, dispatch]);

	// Handle redirect after user data is loaded
	useEffect(() => {
		if (isLoggingOut) {
			return;
		}

		if (auth0IsLoading) {
			return;
		}

		if (!isAuthenticated) {
			if (config.ENABLE_SECURITY_ALERT) {
				alert('About to redirect to /login\n\nState:\n- isAuthenticated: ' + isAuthenticated + '\n- auth0Error: ' + (auth0Error ? JSON.stringify(auth0Error, null, 2) : 'null') + '\n\nCheck console and state before clicking OK');
			}
			// Preserve error/error_description so the login page can show the Auth0 deny message
			history.replace(location.search ? { pathname: '/login', search: location.search } : '/login');
			return;
		}

		if (userIsLoading) {
			return;
		}

		if (userData) {
			logUserLogin();
			
			// Check for return URL
			const returnUrl = localStorage.getItem('returnUrl');
			const safeReturnUrl = getSafeReturnUrl(returnUrl);
			if (safeReturnUrl) {
				// Get token from SDK for cookie
				const setCookieAndRedirect = async () => {
					const token = await getAccessTokenSilently({
						audience: 'https://portal-api'
					});
					if (token) {
						setBinfAuthCookie(token);
					}
					localStorage.removeItem('returnUrl');
					window.location = safeReturnUrl;
				};
				setCookieAndRedirect();
				return;
			}
			localStorage.removeItem('returnUrl');
			
			// Check for state from protected route
			if (history.location.state?.from?.pathname) {
				history.push(history.location.state.from.pathname);
			} else {
				history.push('/home');
			}
		} else if (userError) {
			if (userError?.status === 401) {
				history.replace('/securitycheck');
				return;
			}

			// Check if this is a new signup with unverified email
			// If so, redirect to verify email instead of security check
			const isSignup = localStorage.getItem('signupAction') === 'create';
			if (isAuthenticated && user && isSignup && user.email_verified === false) {
				history.replace("/verifyemail");
				return;
			}

			const errorInfo = {
				message: userError.message,
				status: userError.status,
				data: userError.data,
				url: window.location.href
			};
			if (config.ENABLE_SECURITY_ALERT) {
				alert('About to redirect to /securitycheck\n\nState:\n- isAuthenticated: ' + isAuthenticated + '\n- userError: ' + JSON.stringify(errorInfo, null, 2) + '\n\nCheck console and state before clicking OK');
			}
			history.replace('/securitycheck');
			return;
		}
	}, [isLoggingOut, auth0IsLoading, isAuthenticated, userData, userIsLoading, userError, history, logger, auth0Error, location]);

	const awaitingPortalUser = isAuthenticated && !auth0IsLoading;
	const showLoading =
		auth0IsLoading || (awaitingPortalUser && (userIsLoading || (!userData && !userError)));
	
	// Register loading state with global loading context
	useLoadingState('callback', showLoading);
	
	return (
		<div data-testid="callback">
			{/* Loading is handled by GlobalLoading component */}
		</div>
	);
};

export default Callback;
